import * as React from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import headerRightBg from '../images/app-flow/headerRightBg.png';
import headerRightBg375 from '../images/app-flow/headerRightBg375.png';
import WomanAppProgressBar from '../images/app-flow/WomanAppProgressBar.svg';
import EmailContactSales from '../components/EmailContactSales';
import ProblemSolution from '../components/ProblemSolution';
import FeaturesAndBenefits from '../components/FeaturesAndBenefits';
import FinancialInstitutions from '../components/FinancialInstitutions';
import ManFlagArrow from '../images/app-flow/ManFlagArrow.svg';
import Paperless from '../images/app-flow/Paperless.svg';
import LaptopLock from '../images/app-flow/LaptopLock.svg';
import halfSectionGradient from '../images/app-flow/halfSectionGradient.png';
import fullWidthSplitBg from '../images/app-flow/fullWidthSplitBg.png';
import PhoneFieldsLeafs from '../images/app-flow/PhoneFieldsLeafs.png';
import WantToSeeHow from '../components/WantToSeeHow';

const HeaderContent = styled.article`
	padding-bottom: 1em;
	display: flex;
	flex-direction: column;

	& h1 {
		min-height: 0;
	}

	& p {
		max-width: 490px;
	}
`;

const AppFlowHeaderContent =
	<HeaderContent style={{paddingTop: `80px`}}>
		<h1>AppFlow</h1>
		<p>Streamlined customer<br />application processes</p>
		<EmailContactSales />
	</HeaderContent>
;

const HeaderBottomContentFigure = styled.figure`

	& svg.bottomAligned {
		max-width: 60%;
		margin-top: 2em;
		margin-bottom: 2em;

		@media (${(props) => props.theme.mediumUp}) {
			max-width: 100%;
			margin-top: 0;
			margin-bottom: 1em;
		}

		@media (${(props) => props.theme.largeUp}) {
			max-width: 459px;
		}

		@media (${(props) => props.theme.xLargeUp}) {
			max-width: 100%;
		}
	}
`;

const WomanAppProgressBarFigure =
	<HeaderBottomContentFigure>
		<WomanAppProgressBar alt='Woman next to app with progress bar' className='bottomAligned' />
	</HeaderBottomContentFigure>
;

const Section = styled.section`

	&.full-width-split#appFlowSplit {

		@media (${(props) => props.theme.mediumUp}) {
			& .responsivePadding {
				align-items: stretch;

				& article {
					align-items: center;
					display: flex;
				}
			}
		}

		& figure {
			background-color: #5cd3f2;
			background-image: ${(props) => `url('${halfSectionGradient}')`};
			align-items: center;

			@media (${(props) => props.theme.mediumUp}) {
				padding-left: 30px;
			}

			@media (${(props) => props.theme.largeUp}) {
				padding-left: 40px;
			}

			/* IE 11 */

			@media all and (-ms-high-contrast: none) {
				& img {
					max-width: 367px;
					max-height: 425px;
				}
			}

			@media (${(props) => props.theme.smallUp}) and (-ms-high-contrast: none) {
				& img {
					max-width: 477px;
					max-height: 517px;
				}
			}

			@media (${(props) => props.theme.mediumUp}) and (-ms-high-contrast: none) {
				& img {
					max-width: 294px;
					max-height: 320px;
				}
			}

			@media (${(props) => props.theme.largeUp}) and (-ms-high-contrast: none) {
				& img {
					max-width: 390px;
					max-height: 422px;
				}
			}

			@media (${(props) => props.theme.xLargeUp}) and (-ms-high-contrast: none) {
				& img {
					max-width: 477px;
					max-height: 517px;
				}
			}
		}
	}

	@media (${(props) => props.theme.mediumUp}) {
		&.whiteLeftLtBlueRight {
			background-image: ${(props) => `url('${fullWidthSplitBg}')`};
		}
	}
`;

const AppFlowPage = (props: any) => {
	return (
	<Layout
		headerSplashBg={headerRightBg}
		headerSplashBg375={headerRightBg375}
		headerSplashBgSize={'110% 100%'}
		headerSplashBgSizeLarge={'auto'}
		headerSplashBgPosition={'center top'}
		headerSplashBgPositionLarge={'center top'}
		headerSplashAlignItemsLarge={'flex-end'}
		headerBottomContent={WomanAppProgressBarFigure}
		headerContentClass={'product'}
		headerContent={AppFlowHeaderContent}
		headerContentMaxWidth={'490px'}
		headerSplashBgAverageColor={'white'}
		pageTitle='AppFlow - Online application workflows'
		metaDescription={'Go green and provide your customers with our intuitive, mobile-first application forms for easy account creation anytime, anywhere.'}
		skipMoney2020={true}
		splashHeaderMinHeight={'410px'}
		BodyClass={'AppFlow'}
	>
		<ProblemSolution
			problem={'Many financial institutions are losing customers online without digital application workflows. Additionally, it is difficult to get customers in-branch to fill out paper applications.'}
			solution={'AppFlow provides customers with a simple, online application workflow, making it faster and easier for customers to set up new accounts anytime, anywhere.'}
		/>
		<FeaturesAndBenefits
			solidBgColor = {'#5cd3f2'}

			leftIcon = {<ManFlagArrow alt='man, flag, arrow icon' style={{margin: `5px 0`}} />}
			leftHeading = {'Dynamic,\nguided workflows'}
			leftText={'Increase conversions with intuitive forms tailored to each financial product.'}

			middleIcon = {<Paperless alt='Paperless icon' style={{margin: `13px 0`}} />}
			middleHeading = {'Paperless\napplication forms'}
			middleText = {'Reduce repetitive data entry and human error.'}

			rightIcon = {<LaptopLock alt='Secure Laptop icon' style={{margin: `16px 0`}} />}
			rightHeading = {'Secure data\ninputs'}
			rightText = {'Leverage industry-best data encryption standards.'}
		/>
		<Section className='full-width-split full-width whiteLeftLtBlueRight' id='appFlowSplit'>
			<div className='centeredWrapper'>
				<div className='responsivePadding row row-flush'>
					<article className='col-12 col-md-6'>
						<div className='product text-box'>
							<header>
								<h2>Offer your customers<br />convenient online applications</h2>
							</header>
							<p>Banking is no longer a place that we go, it’s something that we do. Customers are gravitating away from frustrating paper application processes towards financial institutions who are investing in technology.</p>
							<p>Remove the physical barriers from your application process by offering your customers the freedom to apply anywhere, on any device.  AppFlow's convenient online applications meet today’s digital banking demands to set your lending program up for growth.</p>
						</div>
					</article>
					<figure className='col-12 col-md-6 left-on-desktop'>
						<img src={PhoneFieldsLeafs} alt='screenshot smartphones with MK web app' />
					</figure>
				</div>
			</div>
		</Section>
		<div className='centeredWrapper'>
			<FinancialInstitutions />
			<WantToSeeHow />
		</div>
	</Layout>
	);
};

export default AppFlowPage;
